import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '')
  : window.location.hostname === 'localhost'
    ? 'http://localhost:8002'
    : 'https://hwa-nyc-api.fly.dev';

const API_PATH = '/api/v1/geographic';

// Configuración de axios con timeout y headers
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 segundos
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  withCredentials: false
});

// Interceptor para logs de errores detallados
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // La petición fue hecha y el servidor respondió con un código de estado
      // que cae fuera del rango 2xx
      console.error("Error de respuesta:", {
        data: error.response.data,
        status: error.response.status,
        headers: error.response.headers
      });
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      console.error("Error de petición:", error.request);
    } else {
      // Algo ocurrió durante la configuración de la petición que provocó un error
      console.error("Error:", error.message);
    }
    return Promise.reject(error);
  }
);

export interface Area {
  id: string;  // Cambiado a string porque los IDs son strings (e.g., "B1", "N148")
  name: string;
  area_type: string;
  parent_id: string | null;
  population: number;
  geometry: any;
}

export interface AreaStats extends Area {
  total_votes: number;
  positive_votes: number;
  negative_votes: number;
  color: string;
  opacity: number;
  confidence_percentage: number;
  center?: { // El centroide es opcional y tiene formato lng/lat
    lng: number;
    lat: number;
  };
}

export interface GlobalStats {
  total_areas: number;
  total_votes: number;
  positive_votes: number;
  last_map_update: string;
}

const api = {
  // Obtener todas las áreas geográficas
  getAreas: async (areaType?: string): Promise<Area[]> => {
    try {
      const url = areaType 
        ? `${API_PATH}/areas?area_type=${encodeURIComponent(areaType)}` 
        : `${API_PATH}/areas`;
      
      console.log(`Solicitando áreas: ${url}`);
      const response = await axiosInstance.get(url);
      console.log('Respuesta de áreas:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al obtener áreas:', error);
      return [];
    }
  },

  // Obtener estadísticas de un área específica
  getAreaStats: async (areaId: string): Promise<AreaStats> => {
    try {
      const url = `${API_PATH}/areas/${encodeURIComponent(areaId)}/stats`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener estadísticas del área ${areaId}:`, error);
      throw error;
    }
  },

  // Obtener estadísticas de todas las áreas
  getAllAreasStats: async (): Promise<AreaStats[]> => {
    try {
      const url = `${API_PATH}/areas/stats?_ts=${Date.now()}`;
      const response = await axiosInstance.get(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas de todas las áreas:', error);
      return [];
    }
  },

  // Obtener estadísticas globales
  getGlobalStats: async (): Promise<GlobalStats> => {
    try {
      const url = `${API_PATH}/stats?_ts=${Date.now()}`;
      const response = await axiosInstance.get(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas globales:', error);
      return { 
        total_areas: 0, 
        total_votes: 0, 
        positive_votes: 0,
        last_map_update: new Date().toISOString()
      };
    }
  },

  // Votar por un área
  vote: async (areaId: string, isPositive: boolean): Promise<AreaStats> => {
    try {
      const url = `${API_PATH}/areas/${encodeURIComponent(areaId)}/vote`;
      const response = await axiosInstance.post(url, {
        is_positive: isPositive
      });
      return response.data;
    } catch (error) {
      console.error(`Error al votar por el área ${areaId}:`, error);
      throw error;
    }
  }
};

export default api; 